/**
 * Button Style Utilities
 *
 * This file contains utility functions to generate button styles based on Figma XDS design system.
 * All styles are extracted from Figma using Figma MCP to ensure 100% accuracy.
 *
 * Figma References:
 * - Solid Buttons: https://www.figma.com/design/S5vzHnfEkTU1uETKnVOl7x/XDS---Exchange-Design-System?node-id=1577-10578
 * - Ghost Buttons: https://www.figma.com/design/S5vzHnfEkTU1uETKnVOl7x/XDS---Exchange-Design-System?node-id=1577-10703
 */

/**
 * Button variant type
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'warning'
  | 'destructive';

/**
 * Button style mode
 */
export type ButtonMode = 'solid' | 'ghost';

/**
 * Color configuration for each variant
 * Based on Figma XDS design system
 */
interface VariantColorConfig {
  /** Text color (base color, e.g., text-foreground) */
  text: string;
  /** Background color for solid buttons */
  solidBg: string;
  /** Hover background for solid buttons */
  solidHoverBg: string;
  /** Focus ring color */
  focusRing: string;
  /** Ghost hover background (semi-transparent version of the variant color) */
  ghostHoverBg: string;
}

/**
 * Variant color configurations extracted from Figma
 *
 * IMPORTANT MAPPING RULES:
 * 1. Solid buttons use `-foreground` suffix for text (e.g., text-primary-foreground)
 * 2. Ghost buttons use base color for text (e.g., text-primary)
 *    - Exception: Ghost primary uses text-accent-foreground
 * 3. Ghost hover backgrounds use semi-transparent version of the variant color
 *
 * NOTE: Design tokens don't have a "foreground" concept. The frontend codebase
 * establishes semantic mapping: design token "accent" → CSS "text-accent-foreground"
 */
const VARIANT_COLORS: Record<ButtonVariant, VariantColorConfig> = {
  // Primary: Figma var(--primary, #2e92e5)
  primary: {
    text: 'text-accent-foreground',
    solidBg: 'bg-primary',
    solidHoverBg: 'hover:bg-primary/80',
    focusRing:
      'focus-visible:ring-primary/20 dark:focus-visible:ring-primary/40',
    ghostHoverBg: 'hover:bg-accent', // Figma: rgba(137,195,242,0.16) = var(--accent)
  },
  // Secondary: Figma var(--secondary) bg + var(--foreground) text
  secondary: {
    text: 'text-foreground',
    solidBg: 'bg-secondary',
    solidHoverBg: 'hover:bg-secondary/70',
    focusRing: 'focus-visible:ring-secondary/80',
    ghostHoverBg: 'hover:bg-secondary/70',
  },
  // Tertiary: Figma var(--secondary) bg + var(--muted-foreground) text
  tertiary: {
    text: 'text-secondary-foreground',
    solidBg: 'bg-secondary',
    solidHoverBg: 'hover:bg-secondary/70',
    focusRing: 'focus-visible:ring-secondary/80',
    ghostHoverBg: 'hover:bg-secondary/70',
  },
  // Positive: Figma var(--success, #24cc8e)
  success: {
    text: 'text-success-foreground',
    solidBg: 'bg-success',
    solidHoverBg: 'hover:bg-success/80',
    focusRing:
      'focus-visible:ring-success/20 dark:focus-visible:ring-success/40',
    ghostHoverBg: 'hover:bg-opacity-success', // Figma: rgba(25,164,113,0.16)
  },
  // Warning: Figma var(--warning, #facc15)
  warning: {
    text: 'text-warning-foreground',
    solidBg: 'bg-warning',
    solidHoverBg: 'hover:bg-warning/80',
    focusRing:
      'focus-visible:ring-warning/20 dark:focus-visible:ring-warning/40',
    ghostHoverBg: 'hover:bg-opacity-warning', // Figma: rgba(234,179,8,0.16)
  },
  // Destructive: Figma var(--destructive, #f16f6a)
  destructive: {
    text: 'text-destructive-foreground',
    solidBg: 'bg-destructive',
    solidHoverBg: 'hover:bg-destructive/80',
    focusRing:
      'focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
    ghostHoverBg: 'hover:bg-opacity-destructive', // Figma: rgba(241,111,106,0.16)
  },
};

/**
 * Generate background classes for a button variant
 *
 * @param variant - Button variant
 * @param mode - Button mode (solid or ghost)
 * @returns Background class string
 *
 * @example
 * ```ts
 * getBackgroundClasses('primary', 'solid')
 * // => 'bg-primary hover:bg-primary/90'
 *
 * getBackgroundClasses('primary', 'ghost')
 * // => 'bg-transparent hover:bg-accent'
 * ```
 */
export function getBackgroundClasses(
  variant: ButtonVariant,
  mode: ButtonMode,
): string {
  const config = VARIANT_COLORS[variant];

  if (mode === 'solid') {
    return `${config.solidBg} ${config.solidHoverBg}`;
  }

  // Ghost mode: transparent background + hover background
  return `bg-transparent ${config.ghostHoverBg}`;
}

/**
 * Generate text color classes for a button variant
 *
 * @param variant - Button variant
 * @param mode - Button mode (solid or ghost)
 * @returns Text color class string
 *
 * @example
 * ```ts
 * getTextClasses('primary', 'solid')
 * // => 'text-primary-foreground'
 *
 * getTextClasses('primary', 'ghost')
 * // => 'text-primary hover:text-primary'
 * ```
 */
export function getTextClasses(
  variant: ButtonVariant,
  mode: ButtonMode,
): string {
  const config = VARIANT_COLORS[variant];

  if (mode === 'solid') {
    // Solid buttons use -foreground suffix for text color
    // Exception: secondary and tertiary use text-foreground and text-muted-foreground
    if (variant === 'secondary' || variant === 'tertiary') {
      return config.text;
    }
    return 'text-reversed';
  }

  // Ghost buttons use base color for text
  return `${config.text} hover:${config.text}`;
}

/**
 * Generate focus ring classes for a button variant
 *
 * @param variant - Button variant
 * @returns Focus ring class string
 */
export function getFocusRingClasses(variant: ButtonVariant): string {
  return VARIANT_COLORS[variant].focusRing;
}

/**
 * Generate complete button classes for a variant and mode
 *
 * @param variant - Button variant
 * @param mode - Button mode (solid or ghost)
 * @returns Complete class string
 *
 * @example
 * ```ts
 * getButtonClasses('primary', 'solid')
 * // => 'bg-primary hover:bg-primary/90 text-primary-foreground focus-visible:ring-primary/20 dark:focus-visible:ring-primary/40'
 *
 * getButtonClasses('warning', 'ghost')
 * // => 'bg-transparent hover:bg-opacity-warning text-warning hover:text-warning focus-visible:ring-warning/20 dark:focus-visible:ring-warning/40'
 * ```
 */
export function getButtonClasses(
  variant: ButtonVariant,
  mode: ButtonMode,
): string {
  const bg = getBackgroundClasses(variant, mode);
  const text = getTextClasses(variant, mode);
  const focus = getFocusRingClasses(variant);

  return `${bg} ${text} ${focus}`;
}
